import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Grid, Chip, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, TextField, DialogActions } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import axios from 'axios';

const StudentAssignments = () => {
    const { currentUser } = useSelector(state => state.user);
    const classID = currentUser?.sclassName?._id;
    
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
    const [submissionUrl, setSubmissionUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchAssignments();
    }, []);

    const fetchAssignments = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/AssignmentsList/${classID}`);
            if (res.data && Array.isArray(res.data)) {
                setAssignments(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch assignments", err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSubmit = (id) => {
        setSelectedAssignmentId(id);
        setSubmissionUrl("");
        setSubmitDialogOpen(true);
    };

    const handleCloseSubmit = () => {
        setSubmitDialogOpen(false);
        setSelectedAssignmentId(null);
    };

    const handleSubmitUrl = async () => {
        if (!submissionUrl) return;
        try {
            setSubmitting(true);
            await axios.post(`${process.env.REACT_APP_BASE_URL}/SubmitAssignment`, {
                assignmentId: selectedAssignmentId,
                studentId: currentUser._id,
                submissionUrl
            });
            await fetchAssignments(); // Refresh list
            handleCloseSubmit();
        } catch (err) {
            console.error("Submission failed", err);
        } finally {
            setSubmitting(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.4 } }
    };

    const getStudentStatus = (assignment) => {
        const sub = assignment.submissions?.find(s => s.student.toString() === currentUser._id);
        return sub ? 'Submitted' : 'Pending';
    };

    const getStudentSubmission = (assignment) => {
        return assignment.submissions?.find(s => s.student.toString() === currentUser._id);
    };

    const pendingCount = assignments.filter(a => getStudentStatus(a) === 'Pending').length;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
                {/* Header */}
                <Box sx={{ mb: 4, p: 4, borderRadius: '20px', background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', color: '#fff', boxShadow: '0 10px 30px rgba(253, 160, 133, 0.3)' }}>
                    <Typography variant="h4" sx={{ fontWeight: '800', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AssignmentIcon sx={{ fontSize: 40 }} />
                        Active Assignments
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                        {pendingCount === 0 ? "Awesome job! You've completed all your assignments." : `You have ${pendingCount} pending assignment${pendingCount > 1 ? 's' : ''}. Keep up the momentum!`}
                    </Typography>
                </Box>

                {/* Assignment List */}
                <Grid container spacing={3}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : assignments.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 4 }}>
                            <Typography variant="h6" color="textSecondary">No assignments found for this class.</Typography>
                        </Box>
                    ) : (
                        <AnimatePresence>
                            {assignments.map((assignment) => {
                                const status = getStudentStatus(assignment);
                                const submission = getStudentSubmission(assignment);
                                return (
                                <Grid item xs={12} key={assignment._id}>
                                    <motion.div variants={itemVariants} layout initial="hidden" animate="visible" exit={{ opacity: 0, scale: 0.9 }}>
                                        <AssignmentCard elevation={0} isSubmitted={status === 'Submitted'}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                                <Box>
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                        <Chip label={assignment.subject?.subName || 'General'} size="small" sx={{ background: 'rgba(15, 23, 42, 0.05)', color: '#0f172a', fontWeight: 'bold' }} />
                                                        <Chip label="Assignment" size="small" sx={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontWeight: 'bold' }} />
                                                    </Box>
                                                    <Typography variant="h5" sx={{ fontWeight: '700', color: '#1e293b', mb: 1, textDecoration: status === 'Submitted' ? 'line-through' : 'none', opacity: status === 'Submitted' ? 0.6 : 1 }}>
                                                        {assignment.title}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ color: '#475569', mb: 2, maxWidth: '800px' }}>
                                                        {assignment.description}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontWeight: '500' }}>
                                                        <AccessTimeIcon fontSize="small" /> Due: {new Date(assignment.dueDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                                        <span style={{ marginLeft: 8, color: '#0f172a' }}>• Max Marks: {assignment.maxMarks}</span>
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                                    {status === 'Pending' ? (
                                                        <StatusChip icon={<AccessTimeIcon />} label="Pending" />
                                                    ) : (
                                                        <SuccessChip icon={<CheckCircleIcon />} label="Submitted" />
                                                    )}
                                                    
                                                    {status === 'Pending' ? (
                                                        <SubmitButton 
                                                            variant="contained" 
                                                            disableElevation
                                                            onClick={() => handleOpenSubmit(assignment._id)}
                                                        >
                                                            <CloudUploadIcon sx={{ mr: 1 }} /> Submit URL
                                                        </SubmitButton>
                                                    ) : (
                                                        <Button 
                                                            variant="outlined" 
                                                            color="success" 
                                                            size="small" 
                                                            sx={{ borderRadius: '8px', fontWeight: 'bold', textTransform: 'none' }}
                                                            href={submission?.submissionUrl}
                                                            target="_blank"
                                                        >
                                                            View My Submission
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        </AssignmentCard>
                                    </motion.div>
                                </Grid>
                            )})}
                        </AnimatePresence>
                    )}
                </Grid>
            </motion.div>

            {/* Submission Dialog */}
            <Dialog open={submitDialogOpen} onClose={handleCloseSubmit}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Submit Assignment</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Please enter the URL of your work (e.g., Google Drive link, GitHub repository, or live project link). Make sure the permissions allow grading.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="url"
                        label="Submission URL"
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={submissionUrl}
                        onChange={(e) => setSubmissionUrl(e.target.value)}
                        placeholder="https://..."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={handleCloseSubmit} color="inherit" sx={{ fontWeight: 'bold' }}>Cancel</Button>
                    <Button 
                        onClick={handleSubmitUrl} 
                        variant="contained" 
                        disabled={!submissionUrl || submitting}
                        sx={{ background: '#3b82f6', fontWeight: 'bold', px: 3, borderRadius: '8px' }}
                    >
                        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const AssignmentCard = styled(Paper)`
  padding: 24px;
  border-radius: 20px !important;
  border: 1px solid ${props => props.isSubmitted ? 'rgba(16, 185, 129, 0.2)' : 'rgba(0,0,0,0.05)'};
  background: ${props => props.isSubmitted ? '#f8fafc' : '#ffffff'} !important;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03) !important;
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;

  &:hover {
    transform: ${props => props.isSubmitted ? 'none' : 'translateY(-4px)'};
    box-shadow: ${props => props.isSubmitted ? '0 4px 20px rgba(0,0,0,0.03) !important' : '0 12px 30px rgba(0,0,0,0.08) !important'};
  }
`;

const StatusChip = styled(Chip)`
  background: #fffbeb !important;
  color: #d97706 !important;
  font-weight: 700 !important;
  border: 1px solid #fde68a !important;
  
  .MuiChip-icon {
    color: #d97706 !important;
  }
`;

const SuccessChip = styled(Chip)`
  background: #ecfdf5 !important;
  color: #059669 !important;
  font-weight: 700 !important;
  border: 1px solid #a7f3d0 !important;
  
  .MuiChip-icon {
    color: #059669 !important;
  }
`;

const SubmitButton = styled(Button)`
  background: #3b82f6 !important;
  color: white !important;
  border-radius: 12px !important;
  padding: 8px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;

  &:hover {
    background: #2563eb !important;
  }
`;

export default StudentAssignments;
